import React from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Checkbox, Password, TooltipTrigger } from '@bcmi-labs/react-components';
import { useTranslation } from 'react-i18next';

import { useForm, Controller } from 'react-hook-form';

import AlertBox from '../../../common/AlertBox';
import InfoIcon from '../../../common/InfoIcon';

import './RegisterForm.scss';

const RegisterForm = (props) => {
  const { t } = useTranslation('register');

  const { onSubmit, error, loading, showConsent } = props;

  const { handleSubmit, errors, control, setValue, getValues, watch } = useForm();

  const marketingApproval = watch('marketingApproval');

  return (
    <section className="register">
      <h1 className="arduino-typography-large">{t('register:title')}</h1>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-row">
          <Controller
            as={Input}
            name="email"
            control={control}
            defaultValue=""
            label={t('register:email')}
            error={errors.email && errors.email.message}
            isRequired
            rules={{
              required: t('register:fieldRequired'),
              pattern: {
                value: /(^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/,
                message: t('register:emailInvalid'),
              },
            }}
          />
        </div>
        <div className="form-row">
          <Controller
            as={Input}
            name="username"
            control={control}
            defaultValue=""
            label={t('register:username')}
            error={errors.username && errors.username.message}
            isRequired
            rules={{
              required: t('register:fieldRequired'),
              pattern: {
                value: /(^[a-z0-9][a-z0-9_-]{2,}$)/i,
                message: t('register:usernameInvalid'),
              },
            }}
          />
          <div className="tooltip-row-wrapper">
            <TooltipTrigger
              TriggerElement={
                <div>
                  <InfoIcon />
                </div>
              }
              className="password-popover"
              tooltipPlacement="top-start"
              tooltipStrategy="absolute"
              tooltipType={'popover'}
            >
              {t('register:tooltip')}
            </TooltipTrigger>
          </div>
        </div>
        <div className="form-row">
          <Controller
            as={Password}
            name="password"
            control={control}
            infoMsg={t('register:passwordMinLength')}
            defaultValue=""
            label={t('register:password')}
            error={errors.password && errors.password.message}
            isRequired
            rules={{
              required: t('register:fieldRequired'),
              minLength: {
                value: 8,
                message: t('register:passwordMinLength'),
              },
            }}
          />
        </div>
        {showConsent ? (
          <div className="privacy-container">
            <div className="privacy-row">
              <Controller
                render={({ onChange, onBlur, value }) => (
                  <Checkbox
                    label={t('register:privacyMd')}
                    isRequired
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.checked)}
                    isSelected={value}
                    error={errors.privacyApproval && errors.privacyApproval.message}
                  />
                )}
                name="privacyApproval"
                control={control}
                defaultValue=""
                rules={{
                  required: t('register:fieldRequired'),
                }}
              />
            </div>
            <div className="privacy-row">
              <Controller
                render={({ onChange, onBlur, value }) => (
                  <Checkbox
                    label={t('register:newsletter')}
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.checked)}
                    isSelected={value}
                    withoutStatus={true}
                  />
                )}
                name="newsletterApproval"
                control={control}
                defaultValue=""
              />
            </div>
            <div className="privacy-row">
              <Controller
                render={({ onChange, onBlur, value }) => (
                  <Checkbox
                    label={t('register:tracking')}
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.checked)}
                    isSelected={value}
                    withoutStatus={true}
                  />
                )}
                name="trackingApproval"
                control={control}
                defaultValue=""
              />
            </div>
            <div className="privacy-row">
              <Controller
                render={({ onChange, onBlur, value }) => (
                  <Checkbox
                    label={t('register:marketing:approval')}
                    onBlur={onBlur}
                    onChange={(e) => {
                      onChange(e.target.checked);
                      if (!e.target.checked) {
                        setValue('marketingEverything', false);
                        setValue('marketingMaker', false);
                        setValue('marketingEducation', false);
                        setValue('marketingProfessional', false);
                      }
                    }}
                    isSelected={value}
                    withoutStatus={true}
                  />
                )}
                name="marketingApproval"
                control={control}
                defaultValue=""
                rules={{
                  validate: (val) => {
                    const values = getValues();
                    const atLeastOneMarketingChoice = Boolean(
                      values.marketingEverything ||
                        values.marketingMaker ||
                        values.marketingEducation ||
                        values.marketingProfessional
                    );

                    return !val || (val && atLeastOneMarketingChoice) || t('register:marketing:atLeastOneChoice');
                  },
                }}
              />
            </div>
            {errors.marketingApproval && <AlertBox message={errors.marketingApproval.message} />}
            {marketingApproval && (
              <div className="marketing-choices">
                <Controller
                  render={({ onChange, onBlur, value }) => (
                    <Checkbox
                      label={t('register:marketing:everything')}
                      onBlur={onBlur}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue('marketingApproval', e.target.checked);
                        }
                        setValue('marketingMaker', e.target.checked ? '-' : false);
                        setValue('marketingEducation', e.target.checked ? '-' : false);
                        setValue('marketingProfessional', e.target.checked ? '-' : false);
                        onChange(e.target.checked);
                      }}
                      isSelected={value}
                      withoutStatus={true}
                    />
                  )}
                  name="marketingEverything"
                  control={control}
                  defaultValue=""
                />
                <Controller
                  render={({ onChange, onBlur, value }) => (
                    <Checkbox
                      label={t('register:marketing:maker')}
                      isIndeterminate={value === '-'}
                      isDisabled={value === '-'}
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.checked)}
                      isSelected={value && value !== '-'}
                      withoutStatus={true}
                    />
                  )}
                  name="marketingMaker"
                  control={control}
                  defaultValue=""
                />
                <Controller
                  render={({ onChange, onBlur, value }) => (
                    <Checkbox
                      label={t('register:marketing:education')}
                      isIndeterminate={value === '-'}
                      isDisabled={value === '-'}
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.checked)}
                      isSelected={value && value !== '-'}
                      withoutStatus={true}
                    />
                  )}
                  name="marketingEducation"
                  control={control}
                  defaultValue=""
                />
                <Controller
                  render={({ onChange, onBlur, value }) => (
                    <Checkbox
                      label={t('register:marketing:professional')}
                      isIndeterminate={value === '-'}
                      isDisabled={value === '-'}
                      onBlur={onBlur}
                      onChange={(e) => onChange(e.target.checked)}
                      isSelected={value && value !== '-'}
                      withoutStatus={true}
                    />
                  )}
                  name="marketingProfessional"
                  control={control}
                  defaultValue=""
                />
              </div>
            )}
          </div>
        ) : null}
        {error ? <AlertBox message={error} /> : null}
        <Button type="submit" loading={loading}>
          {t('register:submitText')}
        </Button>
      </form>

      <div className="spacer"></div>
    </section>
  );
};

RegisterForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool,
  showConsent: PropTypes.bool.isRequired,
};

export default RegisterForm;
